import React, { useMemo } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import PointsService from '../services/PointsService';
import StudentRepository from '../repositories/StudentRepository';
import { useStudentPortal } from '../store/StudentPortalContext';
import { useSettings } from '../store/SettingsContext';
import { useDisplaySettings } from '../store/DisplaySettingsContext';
import { formatDateTime } from '../utils/dateFormat';

export default function StudentPortalPointsPage() {
  const { student } = useStudentPortal();
  const { settings } = useSettings();
  const { settings: displaySettings } = useDisplaySettings();
  const portalSettings = settings?.studentPortal || {};
  const pointsService = useMemo(() => new PointsService(), []);
  const studentRepository = useMemo(() => new StudentRepository(), []);

  const rewards = Array.isArray(portalSettings.rewards) ? portalSettings.rewards : [];
  const pointsEnabled = Boolean(portalSettings.pointsEnabled);
  const leaderboardEnabled = Boolean(portalSettings.leaderboardEnabled);
  const blurNames = portalSettings.leaderboardBlurNames !== false;
  const pointsTotal = student ? pointsService.getTotalForStudent(student.id) : 0;
  const pointsHistory = useMemo(
    () => (student ? pointsService.listForStudent(student.id) : []),
    [pointsService, student]
  );
  const reducedMotion = Boolean(displaySettings?.reducedMotion);
  const sourceBreakdown = useMemo(() => {
    const totals = pointsHistory.reduce((acc, entry) => {
      const key = entry.source || 'other';
      acc[key] = (acc[key] || 0) + Number(entry.points || 0);
      return acc;
    }, {});
    const entries = Object.entries(totals)
      .filter(([, value]) => Number(value) > 0)
      .map(([key, value]) => ({
        name: key.replace('-', ' '),
        value
      }));
    return entries.length ? entries : [{ name: 'No data', value: 1 }];
  }, [pointsHistory]);

  const weeklyTrend = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 5; i >= 0; i -= 1) {
      const start = new Date(now);
      start.setDate(start.getDate() - i * 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const label = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      buckets.push({ start, end, label, points: 0 });
    }
    pointsHistory.forEach((entry) => {
      const date = new Date(entry.createdAt);
      const bucket = buckets.find((item) => date >= item.start && date <= item.end);
      if (bucket) {
        bucket.points += Number(entry.points || 0);
      }
    });
    return buckets.map((bucket) => ({
      label: bucket.label,
      points: bucket.points
    }));
  }, [pointsHistory]);
  const students = useMemo(() => studentRepository.getAll(), [studentRepository]);
  const leaderboard = useMemo(
    () => pointsService.buildLeaderboard({
      students,
      mode: portalSettings.leaderboardScope || 'class',
      referenceStudent: student
    }),
    [pointsService, students, portalSettings.leaderboardScope, student]
  );
  const sortedRewards = rewards
    .filter((reward) => Number(reward.points) > 0)
    .sort((a, b) => Number(a.points) - Number(b.points));
  const nextReward = sortedRewards.find((reward) => Number(reward.points) > pointsTotal);
  const progressPercent = nextReward ? Math.min(100, Math.round((pointsTotal / nextReward.points) * 100)) : 100;
  const rank = student
    ? leaderboard.findIndex((entry) => entry.student.id === student.id) + 1
    : 0;

  if (!student) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Sign in to view rewards.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Rewards & points</h2>
        <p className="sp-muted">Track your points and see what rewards you can unlock.</p>
      </div>

      {!pointsEnabled && (
        <Card className="p-6">
          <p className="sp-muted">Points are currently disabled by your centre.</p>
        </Card>
      )}

      {pointsEnabled && (
        <>
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="p-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Your points</h3>
                <Badge variant="active">{pointsTotal} pts</Badge>
              </div>
              <div className="mt-3 sp-panel sp-panel--muted p-3 text-sm">
                {nextReward
                  ? `Next reward at ${nextReward.points} points: ${nextReward.reward}`
                  : 'You have unlocked all current rewards.'}
              </div>
              <div className="mt-3">
                <div className="text-xs sp-muted">Progress</div>
                <div className="sp-progress mt-2">
                  <div className="sp-progress__bar" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Reward table</h3>
              <div className="mt-3 space-y-2 text-sm">
                {sortedRewards.length === 0 && <div className="sp-muted">No rewards configured.</div>}
                {sortedRewards.map((reward) => (
                  <div key={reward.id || reward.points} className="sp-panel sp-panel--muted p-3">
                    <div className="flex items-center justify-between">
                      <span className="sp-value" style={{ fontWeight: 600 }}>{reward.reward}</span>
                      <Badge variant={pointsTotal >= reward.points ? 'success' : 'inactive'}>
                        {reward.points} pts
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            {leaderboardEnabled && (
              <Card className="p-5">
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Leaderboard</h3>
                <div className="text-xs sp-muted mt-1">
                  {portalSettings.leaderboardScope === 'overall' ? 'Overall leaderboard' : 'Class leaderboard'}
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  {leaderboard.slice(0, 5).map((entry, index) => (
                    <div key={entry.student.id} className="sp-panel sp-panel--muted p-3 flex items-center justify-between">
                      <span>
                        {index + 1}. {blurNames
                          ? entry.student.id === student.id
                            ? 'You'
                            : `Student ${index + 1}`
                          : entry.student.name}
                      </span>
                      <Badge variant="active">{entry.total} pts</Badge>
                    </div>
                  ))}
                  {rank > 0 && (
                    <div className="text-xs sp-muted mt-2">Your rank: {rank}</div>
                  )}
                </div>
              </Card>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Points breakdown</h3>
                <Badge variant="inactive">{pointsTotal} total</Badge>
              </div>
              <div className="text-xs sp-muted mt-1">How your points were earned recently.</div>
              <div className="mt-4 h-56" aria-label="Points breakdown chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceBreakdown}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      isAnimationActive={!reducedMotion}
                    >
                      {sourceBreakdown.map((entry, index) => (
                        <Cell
                          key={`${entry.name}-${index}`}
                          fill={['#60a5fa', '#34d399', '#fbbf24', '#f87171', '#a78bfa'][index % 5]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 text-xs sp-muted">
                {sourceBreakdown
                  .filter((item) => item.name !== 'No data')
                  .map((item) => `${item.name}: ${item.value}`)
                  .join(' · ') || 'No points data yet.'}
              </div>
            </Card>
            <Card className="p-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Weekly points</h3>
                <Badge variant="inactive">Last 6 weeks</Badge>
              </div>
              <div className="text-xs sp-muted mt-1">A quick view of your weekly progress.</div>
              <div className="mt-4 h-56" aria-label="Weekly points chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyTrend}>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar
                      dataKey="points"
                      fill="#60a5fa"
                      radius={[8, 8, 0, 0]}
                      isAnimationActive={!reducedMotion}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 text-xs sp-muted">
                Highest week: {Math.max(...weeklyTrend.map((item) => item.points)) || 0} pts
              </div>
            </Card>
          </div>

          <Card className="p-6 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>Points history</h3>
                <p className="sp-muted">Recent points awarded.</p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                Back to top
              </Button>
            </div>
            {pointsHistory.length === 0 ? (
              <div className="sp-panel sp-panel--muted p-3 text-sm">No points yet.</div>
            ) : (
              <div className="space-y-2">
                {pointsHistory.map((entry) => (
                  <div key={entry.id} className="sp-panel sp-panel--muted p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="sp-value" style={{ fontWeight: 600 }}>{entry.reason}</div>
                      <Badge variant={entry.points > 0 ? 'success' : 'inactive'}>
                        {entry.points > 0 ? `+${entry.points}` : entry.points}
                      </Badge>
                    </div>
                    <div className="text-xs sp-muted">
                      {entry.createdBy} · {formatDateTime(entry.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
